/* eslint-disable @typescript-eslint/no-unused-vars */
import { useGoogleAnalyticsContext } from "contexts";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import { useAppDispatch, useAppSelector, usePopupsState } from "hooks";
import { useLinks } from "hooks/useLinks";
import { PBV3Response } from "interfaces";
import moment from "moment";
import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
	usePbDisplayedMutation,
	usePbGetInactiveTrackerMutation,
	usePostPbMetadataMutation
} from "redux/apis";
import {
	onOpenPopUp,
	onPBV3change,
	selectPoisBountyV3State
} from "redux/slices";
import { getTimeLeft } from "utils/time";
import { getEventTimes, toTimeMs } from "./helper";
import { getAllRewardsCollected } from "../helper";

export const useHandlePBLogic = (isPageVariant: boolean): void => {
	const links = useLinks();
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { pathname } = useLocation();
	const [pbDisplayed] = usePbDisplayedMutation();
	const [postPbMetadata] = usePostPbMetadataMutation();
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const [pbGetInactiveTracker] = usePbGetInactiveTrackerMutation();
	const { nowTime, userTracker, postUserMetadata } = useWebStoreContext();
	const {
		isAnyPopupOpen,
		isMegaMenuOpen,
		isSignupChecking,
		IsInAppToWebDipChecking,
		IsFriendReferralAcceptInviteChecking
	} = usePopupsState();
	const {
		data,
		tracker,
		trackers,
		eventEnded,
		eventActive,
		rewardToClaim,
		inactiveTrackers,
		hasSeenClaimedPopup,
		allRewardsCollected
	} = useAppSelector(selectPoisBountyV3State);

	// if the user is on the /pois-bounty page we redirect to landing page. Because
	// if there's not event active it makes no sense keep the user on the PB page
	const disablePBEvent = useCallback((): void => {
		dispatch(onPBV3change({ eventActive: false }));
		if (pathname === links.pois_bounty.path) {
			navigate(links.home.path);
		}
	}, []);

	const enablePBEvent = useCallback((): void => {
		dispatch(onPBV3change({ eventActive: true, variant: "in-progress" }));
	}, []);

	// has seen Poi's Tutorial PopUp
	useEffect(() => {
		if (
			!isPageVariant ||
			Object.keys(data?.trackersData || [])?.length !== 1 ||
			location.pathname !== links.pois_bounty.path
		) {
			return;
		}
		const hasSeenPBTutorial = async () => {
			if (
				!isAnyPopupOpen &&
				!isSignupChecking &&
				userTracker?.metadata?.hasSeenPBTutorial !== "true"
			) {
				dispatch(
					onOpenPopUp(
						trackers?.eventType === "user" ? "pb-pbftue-v2" : "pb-pbftue-v3"
					)
				);
				await postUserMetadata({ key: "hasSeenPBTutorial", value: "true" });
			}
		};
		hasSeenPBTutorial();
	}, [
		isAnyPopupOpen,
		isSignupChecking,
		location.pathname,
		trackers?.eventType,
		userTracker?.metadata?.hasSeenPBTutorial,
		Object.keys(data?.trackersData || [])?.length
	]);

	useEffect(() => {
		if (isPageVariant || Object.keys(data?.trackersData || [])?.length !== 1) {
			return;
		}
		// set displayed data once per event
		const setDisplayedData = async () => {
			if (!trackers?.eventId) {
				return;
			}
			await pbDisplayed({ eventId: trackers.eventId }).unwrap();
			await postPbMetadata({
				eventId: trackers.eventId,
				key: "setDisplayedForFirstTime",
				value: true
			}).unwrap();
		};
		// set report event start only once per event
		const reportStartEvent = async () => {
			if (!trackers?.eventId) {
				return;
			}
			const timeLeft = getTimeLeft(nowTime, tracker?.endTime, "seconds");
			const timeTotal =
				getTimeLeft(tracker?.startTime, tracker?.endTime, "seconds") ||
				undefined;
			await postPbMetadata({
				eventId: trackers.eventId,
				key: "setStartedEventForFirstTime",
				value: true
			}).unwrap();
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "event_user_start",
				event_type: "pois_bounty_v3",
				action: "pb_user_start",
				source: "pois_bounty_v3",
				type: trackers?.eventType === "user" ? "personal" : "club",
				time_left: timeLeft,
				time_total: timeTotal,
				event_id: trackers?.eventId
			});
		};
		const run = async () => {
			if (trackers?.userData?.metadata?.setDisplayedForFirstTime !== "true") {
				await setDisplayedData();
			}
			if (
				trackers?.userData?.metadata?.setStartedEventForFirstTime !== "true"
			) {
				await reportStartEvent();
			}
		};
		run();
	}, [Object.keys(data?.trackersData || [])?.length]);

	// reset HasSeenClubLeavePopup
	useEffect(() => {
		if (isPageVariant) {
			return;
		}
		const resetHasSeenClubLeavePopup = async () => {
			if (
				trackers?.eventType === "club" &&
				trackers?.userData?.latestEventSelection?.nonSelectedEventType ===
					"user" &&
				trackers?.userData?.metadata?.hasSeenClubLeavePopup === "true"
			) {
				await postPbMetadata({
					eventId:
						trackers?.userData?.latestEventSelection?.nonSelectedEventId ?? "",
					key: "hasSeenClubLeavePopup",
					value: false
				});
			}
		};
		resetHasSeenClubLeavePopup();
	}, [
		trackers?.eventType,
		trackers?.userData?.latestEventSelection?.nonSelectedEventType,
		trackers?.userData?.metadata?.hasSeenClubLeavePopup
	]);

	useEffect(() => {
		if (
			isPageVariant ||
			IsInAppToWebDipChecking ||
			IsFriendReferralAcceptInviteChecking
		) {
			return;
		}
		const hasSeenClubLeavePopup = async () => {
			if (
				trackers?.userData?.latestEventSelection?.selectedEventType &&
				trackers?.eventType !==
					trackers.userData.latestEventSelection.selectedEventType &&
				trackers?.userData?.metadata?.hasSeenClubLeavePopup !== "true"
			) {
				const hasInactiveEvent = await pbGetInactiveTracker({
					activeOnly: false
				})
					.unwrap()
					.then((res: PBV3Response) => {
						const event =
							Object.values(res?.trackersData || {}).filter(
								(ts) => ts.completedDate
							)?.[0] || undefined;
						dispatch(
							onPBV3change({
								inactiveTrackers: event
							})
						);
						return event;
					});
				// if event completed show rewards uncollected popup and then club update
				// if not show club change update
				if (!hasInactiveEvent) {
					await postPbMetadata({
						eventId: trackers?.eventId ?? "",
						key: "hasSeenClubLeavePopup",
						value: true
					});
					dispatch(onOpenPopUp("pb-v3-club-change::0"));
				} else {
					dispatch(onOpenPopUp("pb-v3-rewards-uncollected"));
				}
			}
		};
		hasSeenClubLeavePopup();
	}, [
		trackers?.eventType,
		trackers?.userData?.metadata?.hasSeenClubLeavePopup,
		trackers?.userData?.latestEventSelection?.selectedEventType,
		IsInAppToWebDipChecking,
		IsFriendReferralAcceptInviteChecking
	]);

	useEffect(() => {
		if (
			isPageVariant ||
			IsInAppToWebDipChecking ||
			IsFriendReferralAcceptInviteChecking
		) {
			return;
		}
		const displayedPbEvent = async (
			clubId?: string,
			oldClubId?: string,
			clubName = "",
			eventId = ""
		) => {
			if (oldClubId && clubId !== oldClubId) {
				dispatch(onPBV3change({ clubName }));
				await pbDisplayed({ eventId }).unwrap();
				dispatch(onOpenPopUp("pb-v3-club-change::1"));
			}
		};
		if (trackers) {
			const { clubId, clubName, eventId, userData } = trackers;
			displayedPbEvent(
				clubId,
				userData?.lastDisplayedClubId,
				clubName,
				eventId
			);
		}
	}, [
		trackers?.clubId,
		trackers?.userData?.lastDisplayedClubId,
		IsInAppToWebDipChecking,
		IsFriendReferralAcceptInviteChecking
	]);

	useEffect(() => {
		if (isPageVariant) {
			return;
		}
		dispatch(
			onPBV3change({
				canContribute:
					trackers?.canContribute && eventActive && !allRewardsCollected
			})
		);
	}, [trackers?.canContribute, eventActive, allRewardsCollected]);

	useEffect(() => {
		if (
			isPageVariant ||
			IsInAppToWebDipChecking ||
			IsFriendReferralAcceptInviteChecking
		) {
			return;
		}
		// timeout to avoid dispatching the action before the
		// claim reward flow finish, because of trackers update
		const timeout = setTimeout(async () => {
			if (
				!rewardToClaim &&
				trackers?.userData?.hasUnclaimedRewards &&
				!isAnyPopupOpen &&
				!isMegaMenuOpen &&
				!tracker?.completed
			) {
				const lastSawUncollectedRewardsPopUp =
					trackers?.userData?.cycleData?.[tracker?.cycleIndex ?? 0]?.metadata
						?.sawUncollectedRewardsPopUp;
				const nowTimeMs = toTimeMs(nowTime?.toISOString());
				const lastSawTimeMs = toTimeMs(lastSawUncollectedRewardsPopUp);
				const hasPassedOneHourSinceLastPopup =
					!Number.isNaN(nowTimeMs) &&
					!Number.isNaN(lastSawTimeMs) &&
					nowTimeMs - lastSawTimeMs > 60 * 60 * 1000;
				if (hasPassedOneHourSinceLastPopup) {
					await postPbMetadata({
						eventId: trackers?.eventId ?? "",
						key: "sawUncollectedRewardsPopUp",
						value: nowTime?.toISOString() || new Date().toISOString(),
						cycleIndex: tracker?.cycleIndex || 0
					});
					dispatch(onOpenPopUp("pb-v3-rewards-uncollected"));
				}
			}
		}, 300);

		return () => clearTimeout(timeout);
	}, [
		rewardToClaim,
		trackers?.userData?.hasUnclaimedRewards,
		trackers?.userData?.cycleData?.[tracker?.cycleIndex ?? 0]?.metadata
			?.sawUncollectedRewardsPopUp,
		isAnyPopupOpen,
		isMegaMenuOpen,
		tracker?.completed,
		IsInAppToWebDipChecking,
		IsFriendReferralAcceptInviteChecking
	]);

	useEffect(() => {
		if (
			!Object.keys(data || {}).length ||
			!userTracker ||
			!tracker ||
			isPageVariant
		) {
			return;
		}

		const { endTime } = tracker;
		// Get the current state of the event by dates
		const { eventInProgress } = getEventTimes(nowTime, moment(endTime));

		if (hasSeenClaimedPopup) {
			if (getAllRewardsCollected(tracker)) {
				dispatch(onPBV3change({ allRewardsCollected: true }));
				reportPlaygamiEvent("tp.web_frontend", {
					event_name: "event_user_end",
					event_type: "pois_bounty_v3",
					action: "pb_user_end",
					source: "pois_bounty_v3",
					section_name: "claimed_all_rewards",
					type: trackers?.eventType === "user" ? "personal" : "club",
					event_id: tracker?.eventId
				});
			}
		}

		if (eventInProgress || !endTime) {
			enablePBEvent();
			return;
		}

		disablePBEvent();
	}, [tracker, trackers, eventEnded, hasSeenClaimedPopup]);
};
